/**
 * FlexibleCRUD adapter for tenant Agent Catalog (marketplace browse + deploy).
 * Uses published public platform agents only — not the Platform Admin factory list.
 */

import { getAgent, listAgents } from '@/services/aiStudioAgentService';
import type { AiStudioAgent } from '@/types/aiStudioAgent';

type ListParams = {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

function matchesSearch(agent: AiStudioAgent, term: string): boolean {
  const hay = `${agent.code} ${agent.name} ${agent.domain ?? ''} ${agent.description ?? ''}`.toLowerCase();
  return hay.includes(term);
}

function sortAgents(
  agents: AiStudioAgent[],
  sortBy?: string,
  sortOrder: 'asc' | 'desc' = 'desc'
): AiStudioAgent[] {
  const field = sortBy ?? 'updatedAt';
  const dir = sortOrder === 'asc' ? 1 : -1;
  return [...agents].sort((a, b) => {
    const av = a[field as keyof AiStudioAgent];
    const bv = b[field as keyof AiStudioAgent];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      return av.localeCompare(bv) * dir;
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

/** Tenant marketplace — published catalog agents only. */
export const aiStudioAgentsMarketplaceCRUDService = {
  async getAll(params?: ListParams | string) {
    const query: ListParams = typeof params === 'string' ? { search: params } : params ?? {};

    let agents = await listAgents({ status: 'published', audience: 'catalog' });
    agents = agents.filter((agent) => agent.visibility === 'public');

    const term = query.search?.trim().toLowerCase();
    if (term) {
      agents = agents.filter((agent) => matchesSearch(agent, term));
    }

    agents = sortAgents(agents, query.sortBy ?? 'name', query.sortOrder ?? 'asc');

    const page = Math.max(1, query.page ?? 1);
    const limit = Math.max(1, query.limit ?? 20);
    const total = agents.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit;
    const data = agents.slice(start, start + limit);

    return {
      data,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
  },

  async getById(id: string) {
    return getAgent(id);
  },
};
